/*--------------------------------
Waypoint の置き換え（IntersectionObserver）

offset: 画面上端からの位置（'80%' なら画面の 80% の線）
要素の上端がその線より上に来たら handler('down', el)、
線より下に戻ったら handler('up', el)。
Waypoint と同じく、読み込み時にすでに線より上にある要素には 'down' を呼ぶ。
--------------------------------*/

const observers = {};

function getObserver(offset) {
	if (observers[offset]) return observers[offset];

	const bottom = parseFloat(offset) - 100;
	const handlers = new WeakMap();
	const states = new WeakMap();

	const io = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			const prev = states.get(entry.target);
			const now = entry.isIntersecting;
			states.set(entry.target, now);

			// 初回で線より下にある場合は何もしない（Waypoint と同じ）
			if (prev === undefined && !now) return;
			if (prev === now) return;

			handlers.get(entry.target).forEach((fn) => fn(now ? 'down' : 'up', entry.target));
		});
	}, {
		// 上方向は無限遠扱い、下方向は offset の線まで
		rootMargin: `99999px 0px ${bottom}% 0px`,
	});

	observers[offset] = {
		observe(el, fn) {
			if (!handlers.has(el)) handlers.set(el, []);
			handlers.get(el).push(fn);
			io.observe(el);
		},
	};
	return observers[offset];
}

export function inview(elements, handler, offset = '80%') {
	if (!elements) return;
	const list = elements instanceof Element ? [elements] : Array.from(elements);
	const observer = getObserver(offset);
	list.forEach((el) => observer.observe(el, handler));
}

// 動画の 1 フレーム目が出るまで待つ（遅い回線でも演出が止まり続けないよう 3 秒で打ち切り）
function videoReady(video) {
	// まだ再生も読み込みも始まっていない（preload="none"）ときだけ読み込みを始める。
	// 再生中に load() すると再生が止まるため
	if (video.paused && video.readyState === 0) {
		video.preload = 'auto';
		video.load();
	}

	return Promise.race([
		new Promise((res) => {
			video.addEventListener('loadeddata', res, { once: true });
			video.addEventListener('error', res, { once: true });
		}),
		new Promise((res) => setTimeout(res, 3000)),
	]);
}

// 中の画像・動画が読み込み終わるまで待つ（マスクで画像を見せる演出が、画像の到着前に終わってしまわないように）
function imagesReady(el) {
	const imgs = el.tagName === 'IMG' ? [el] : Array.from(el.querySelectorAll('img'));
	const pending = imgs.filter((img) => !img.complete);
	const videos = Array.from(el.querySelectorAll('video')).filter((video) => video.readyState < 2);
	if (!pending.length && !videos.length) return null;

	return Promise.all(videos.map(videoReady).concat(pending.map((img) => {
		// lazy の画像も、線を越えた時点で読み込みを始めさせる
		img.loading = 'eager';
		return img.decode ? img.decode().catch(() => {}) : new Promise((res) => {
			img.addEventListener('load', res, { once: true });
			img.addEventListener('error', res, { once: true });
		});
	})));
}

// 線を越えたら active を付け外しするだけのよくあるパターン
export function toggleActive(elements, offset = '80%', className = 'active') {
	const isDown = new WeakMap();

	inview(elements, (direction, el) => {
		isDown.set(el, direction === 'down');

		if (direction !== 'down') {
			el.classList.remove(className);
			return;
		}

		const ready = imagesReady(el);
		if (!ready) {
			el.classList.add(className);
			return;
		}

		ready.then(() => {
			// 待っている間に線より下へ戻っていたら付けない
			if (isDown.get(el)) el.classList.add(className);
		});
	}, offset);
}
